import { useEffect, useRef } from 'react';
import { Runner } from '@/game/three/Runner';
import { gameBus, GameEvent } from '@/game/events';

/**
 * Mounts the game into a div and owns its lifecycle, including input. React renders the HUD; the
 * engine renders the game. They never render into each other — that separation is what keeps the
 * frame rate stable.
 *
 * The effect has NO dependencies on purpose: the engine is created exactly once. Depending on a
 * prop callback would tear the game down and rebuild it on every parent render, and the HUD
 * re-renders on every score tick.
 */
export function GameCanvas({ onReady }: { onReady?: (runner: Runner) => void }) {
	const containerRef = useRef<HTMLDivElement>(null);
	const onReadyRef = useRef(onReady);
	onReadyRef.current = onReady;

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const runner = new Runner(container);
		onReadyRef.current?.(runner);
		runner.start();

		// Touch: a tap is a lane change or a jump depending on where it lands; a swipe is explicit.
		let startX = 0;
		let startY = 0;
		let startedAt = 0;
		const onPointerDown = (e: PointerEvent) => {
			startX = e.clientX;
			startY = e.clientY;
			startedAt = performance.now();
		};
		const onPointerUp = (e: PointerEvent) => {
			const dx = e.clientX - startX;
			const dy = e.clientY - startY;
			const travelled = Math.hypot(dx, dy);
			const rect = container.getBoundingClientRect();
			// Short and still = tap; anything with travel is read as a swipe.
			if (travelled < 24 && performance.now() - startedAt < 400) {
				runner.handlePointer(e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height);
			} else {
				runner.handleSwipe(dx, dy);
			}
		};
		const onKeyDown = (e: KeyboardEvent) => {
			if ([' ', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'w', 'a', 'd'].includes(e.key)) e.preventDefault();
			runner.handleKey(e.key);
		};

		container.addEventListener('pointerdown', onPointerDown);
		container.addEventListener('pointerup', onPointerUp);
		window.addEventListener('keydown', onKeyDown);

		// Pause when backgrounded: a game that keeps simulating drains the battery and desyncs score.
		const onVisibility = () => {
			if (document.hidden) {
				runner.pause();
				gameBus.emit(GameEvent.Status, 'paused');
			}
		};
		document.addEventListener('visibilitychange', onVisibility);

		return () => {
			container.removeEventListener('pointerdown', onPointerDown);
			container.removeEventListener('pointerup', onPointerUp);
			window.removeEventListener('keydown', onKeyDown);
			document.removeEventListener('visibilitychange', onVisibility);
			runner.dispose();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- create once; see onReadyRef above
	}, []);

	return <div ref={containerRef} className="absolute inset-0 touch-none select-none" />;
}
